/**

 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage  All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import isApiError from "@insite/client-framework/Common/isApiError";
import { Dictionary } from "@insite/client-framework/Common/Types";
import {
    ApiParameter,
    doesNotHaveExpand,
    patch,
    request,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { Cart, GetCartApiParameter } from "@insite/client-framework/Services/CartService";
import { CartModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

export interface UpdateCartCertificationApiParameter {
    ids: string[];
    eisgcgCertification: boolean;
    mfrCertification: boolean;
}

const cartsUrl = "/api/v1/carts";

const updateCartCertificationUri = "api/custom/cart/updateCartLinesCustomProperties";

export async function updateCartLinesCustomProperties(
    parameter: UpdateCartCertificationApiParameter,
): Promise<ServiceResult<any>> {
    const { ids, eisgcgCertification, mfrCertification } = parameter;
    const patchModel = {
        CartLinesIds: ids,
        EisgcgCertification: eisgcgCertification,
        MfrCertification: mfrCertification,
    };

    try {
        const result = await patch<any>(`${updateCartCertificationUri}`, patchModel);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        throw error;
    }
}

export function get<T>(endpoint: string, parameter: ApiParameter = {}, queryStringParameters: Dictionary<any> = {}) {
    let queryString = "";
    const combinedQueryString: Dictionary<any> = {
        ...parameter,
        ...parameter.additionalQueryStringParameters,
        ...queryStringParameters,
    };

    const additionalExpands = combinedQueryString["additionalExpands"];
    if (additionalExpands) {
        if (!combinedQueryString["expand"]) {
            combinedQueryString["expand"] = [];
        }
        combinedQueryString["expand"] = combinedQueryString["expand"].concat(additionalExpands);
        delete combinedQueryString["additionalExpands"];
    }

    for (const key in combinedQueryString) {
        if (key !== "additionalQueryStringParameters" && combinedQueryString[key] !== undefined) {
            if (key !== "expand" && key !== "exclude" && Array.isArray(combinedQueryString[key])) {
                // WebApi requires arrays to be split into individual query string values
                combinedQueryString[key].forEach((p: string) => {
                    queryString += `${key}=${encodeURIComponent(p)}&`;
                });
            } else {
                queryString += `${key}=${encodeURIComponent(combinedQueryString[key])}&`;
            }
        }
    }

    if (queryString !== "") {
        queryString = (endpoint.indexOf("?") < 0 ? "?" : "&") + queryString.substr(0, queryString.length - 1);
    }

    return request<T>(endpoint + queryString, "GET", { "cache-control": "no-cache" });
}

export async function getCart(parameter: GetCartApiParameter) {
    const newParameter = { ...parameter };
    delete (newParameter as Partial<GetCartApiParameter>).cartId;
    const cartModel = await get<CartModel>(`${cartsUrl}/${parameter.cartId}`, newParameter);
    if (cartModel) {
        const cartResult = cleanCart(cartModel, parameter);
        return cartResult;
    }
}

function cleanCart(cartModel: CartModel, parameter?: { expand?: string[]; additionalExpands?: string[] }) {
    cartModel.orderDate = cartModel.orderDate! && new Date(cartModel.orderDate!);
    cartModel.requestedPickupDateDisplay =
        cartModel.requestedPickupDateDisplay! && new Date(cartModel.requestedPickupDateDisplay!);
    cartModel.requestedDeliveryDateDisplay =
        cartModel.requestedDeliveryDateDisplay! && new Date(cartModel.requestedDeliveryDateDisplay!);

    if (doesNotHaveExpand(parameter, "cartLines")) {
        delete cartModel.cartLines;
    }
    if (doesNotHaveExpand(parameter, "paymentOptions")) {
        delete cartModel.paymentMethod;
        delete cartModel.paymentOptions;
    }
    if (doesNotHaveExpand(parameter, "carriers")) {
        delete cartModel.carriers;
    }
    if (doesNotHaveExpand(parameter, "validation")) {
        delete cartModel.billTo?.validation;
        delete (cartModel.shipTo as Partial<ShipToModel>)?.validation;
    }
    const cartResult = {
        cart: cartModel as Cart,
        billTo: cartModel.billTo ? cartModel.billTo : undefined,
        shipTo: cartModel.shipTo ? cartModel.shipTo : undefined,
    };

    if (cartModel.billTo) {
        cartResult.cart.billToId = cartModel.billTo.id;
        delete (cartResult.cart as CartModel).billTo;
    }

    if (cartModel.shipTo) {
        cartResult.cart.shipToId = cartModel.shipTo.id;
        delete (cartResult.cart as CartModel).shipTo;
    }

    return cartResult;
}
